/**
 * Centralized Logging Service
 * 
 * Provides structured logging with:
 * - Log levels (debug, info, warn, error)
 * - Context tagging (service/component name)
 * - Environment-aware output (verbose in dev, minimal in prod)
 * - Structured data support
 * - Future: remote logging capability
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  service?: string;
  component?: string;
  action?: string;
  [key: string]: unknown;
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  data?: unknown;
  timestamp: string;
  error?: Error;
}

// Log level priority (lower = more verbose)
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

// Environment detection
const isDev = typeof window !== 'undefined' 
  ? window.location.hostname === 'localhost' || window.location.hostname.includes('preview')
  : process.env.NODE_ENV !== 'production';

// Minimum log level based on environment
const MIN_LOG_LEVEL: LogLevel = isDev ? 'debug' : 'warn';

class Logger {
  private context: LogContext;
  private minLevel: LogLevel;

  constructor(context: LogContext = {}, minLevel: LogLevel = MIN_LOG_LEVEL) {
    this.context = context;
    this.minLevel = minLevel;
  }

  /**
   * Create a child logger with additional context
   */
  child(additionalContext: LogContext): Logger {
    return new Logger(
      { ...this.context, ...additionalContext },
      this.minLevel
    );
  }

  /**
   * Create a logger for a specific service
   */
  static forService(serviceName: string): Logger {
    return new Logger({ service: serviceName });
  }

  /**
   * Create a logger for a specific component
   */
  static forComponent(componentName: string): Logger {
    return new Logger({ component: componentName });
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this.minLevel];
  }

  private formatMessage(entry: LogEntry): string {
    const parts: string[] = [];
    
    // Add context prefix
    if (entry.context?.service) {
      parts.push(`[${entry.context.service}]`);
    } else if (entry.context?.component) {
      parts.push(`[${entry.context.component}]`);
    }
    
    if (entry.context?.action) {
      parts.push(`(${entry.context.action})`);
    }
    
    parts.push(entry.message);
    
    return parts.join(' ');
  }

  private log(level: LogLevel, message: string, data?: unknown, error?: Error): void {
    if (!this.shouldLog(level)) {
      return;
    }

    const entry: LogEntry = {
      level,
      message,
      context: this.context,
      data,
      timestamp: new Date().toISOString(),
      error,
    };

    const formattedMessage = this.formatMessage(entry);

    // Output to console with appropriate method
    switch (level) {
      case 'debug':
        if (data !== undefined) {
          console.debug(formattedMessage, data);
        } else {
          console.debug(formattedMessage);
        }
        break;
      case 'info':
        if (data !== undefined) {
          console.info(formattedMessage, data);
        } else {
          console.info(formattedMessage);
        }
        break;
      case 'warn':
        if (data !== undefined) {
          console.warn(formattedMessage, data);
        } else {
          console.warn(formattedMessage);
        }
        break;
      case 'error':
        if (error) {
          console.error(formattedMessage, error, data);
        } else if (data !== undefined) {
          console.error(formattedMessage, data);
        } else {
          console.error(formattedMessage);
        }
        break;
    }

    // Future: Send to remote logging service
    // this.sendToRemote(entry);
  }

  debug(message: string, data?: unknown): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: unknown): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: unknown): void {
    this.log('warn', message, data);
  }

  error(message: string, errorOrData?: Error | unknown, data?: unknown): void {
    if (errorOrData instanceof Error) {
      this.log('error', message, data, errorOrData);
    } else {
      this.log('error', message, errorOrData);
    }
  }

  /**
   * Log with explicit action context
   */
  action(actionName: string): Logger {
    return this.child({ action: actionName });
  }
}

// Default logger instance
export const logger = new Logger();

// Named exports for common patterns
export const createLogger = (context: LogContext): Logger => new Logger(context);
export const serviceLogger = (name: string): Logger => Logger.forService(name);
export const componentLogger = (name: string): Logger => Logger.forComponent(name);

// Export class for advanced usage
export { Logger };

// Convenience type for logging functions
export type LogFn = (message: string, data?: unknown) => void;

